import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import VoiceInput from '../components/VoiceInput';
import ImportarComIA from '../components/ImportarComIA';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Save, Loader2, AlertCircle, UserCheck, Plus, Search, Mic } from 'lucide-react';
import { toast } from 'sonner';
import { validateCPF, formatCPF, buscarCEP } from '../lib/utils';

const NovoPaciente = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [cpfSearching, setCpfSearching] = useState(false);
  const [error, setError] = useState('');
  const [cpfError, setCpfError] = useState('');
  const [cepError, setCepError] = useState('');
  const [existingPatient, setExistingPatient] = useState(null);
  const [existingInternacao, setExistingInternacao] = useState(null);
  
  const [formData, setFormData] = useState({
    nome: '',
    cpf: '',
    data_nascimento: '',
    telefone: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
    observacoes: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'cpf') {
      setExistingPatient(null);
      setExistingInternacao(null);
      setError('');
      const cleaned = value.replace(/\D/g, '');
      if (cleaned.length === 11) {
        if (!validateCPF(cleaned)) {
          setCpfError('CPF inválido');
        } else {
          setCpfError('');
          checkExistingCPF(cleaned);
        }
      } else {
        setCpfError('');
      }
    }
    
    if (field === 'cep') {
      const cleaned = value.replace(/\D/g, '');
      if (cleaned.length === 8) {
        handleCEPLookup(cleaned);
      }
    }
  };

  const checkExistingCPF = async (cpf) => {
    setCpfSearching(true);
    try {
      const response = await api.get(`/pacientes/buscar-cpf/${cpf}`);
      if (response.data.found) {
        setExistingPatient(response.data.paciente);
        setExistingInternacao(response.data.internacao_ativa);
        
        // Auto-fill patient data
        const p = response.data.paciente;
        setFormData(prev => ({
          ...prev,
          nome: p.nome || prev.nome,
          data_nascimento: p.data_nascimento || prev.data_nascimento,
          telefone: p.telefone || prev.telefone,
          cep: p.cep || prev.cep,
          logradouro: p.logradouro || prev.logradouro,
          numero: p.numero || prev.numero,
          complemento: p.complemento || prev.complemento,
          bairro: p.bairro || prev.bairro,
          cidade: p.cidade || prev.cidade,
          uf: p.uf || prev.uf,
          observacoes: p.observacoes || prev.observacoes
        }));
        
        toast.info('Paciente já cadastrado! Dados preenchidos automaticamente.');
      } else {
        setExistingPatient(null);
        setExistingInternacao(null);
      }
    } catch (error) {
      console.error('Error checking CPF:', error);
    } finally {
      setCpfSearching(false);
    }
  };

  const handleCEPLookup = async (cep) => {
    setCepLoading(true);
    setCepError('');
    
    const result = await buscarCEP(cep);
    
    if (result.error) {
      setCepError(result.error);
    } else {
      setFormData(prev => ({
        ...prev,
        logradouro: result.logradouro || prev.logradouro,
        bairro: result.bairro || prev.bairro,
        cidade: result.cidade || prev.cidade,
        uf: result.uf || prev.uf
      }));
      toast.success('Endereço preenchido automaticamente!');
    }
    
    setCepLoading(false);
  };

  const handleCPFBlur = () => {
    const cleaned = formData.cpf.replace(/\D/g, '');
    if (cleaned.length === 11) {
      setFormData(prev => ({ ...prev, cpf: formatCPF(cleaned) }));
    }
  };

  const formatCEP = (cep) => {
    const cleaned = cep.replace(/\D/g, '');
    if (cleaned.length >= 5) {
      return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 8)}`;
    }
    return cleaned;
  };

  const handleCEPBlur = () => {
    const cleaned = formData.cep.replace(/\D/g, '');
    if (cleaned.length === 8) {
      setFormData(prev => ({ ...prev, cep: formatCEP(cleaned) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const cpfClean = formData.cpf.replace(/\D/g, '');
    if (!validateCPF(cpfClean)) {
      setError('CPF inválido');
      return;
    }

    if (!formData.nome.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    if (existingPatient) {
      navigate(`/pacientes/${existingPatient.id}/nova-internacao`);
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/pacientes', {
        ...formData,
        cpf: cpfClean,
        cep: formData.cep.replace(/\D/g, '')
      });
      
      toast.success('Paciente cadastrado com sucesso!');
      navigate(`/pacientes/${response.data.id}/nova-internacao`);
    } catch (err) {
      if (err.response?.status === 400) {
        const detail = err.response?.data?.detail || '';
        if (detail.includes('CPF já cadastrado')) {
          try {
            const searchRes = await api.get(`/pacientes?search=${cpfClean}`);
            const existing = searchRes.data.find(p => p.cpf.replace(/\D/g, '') === cpfClean);
            if (existing) {
              setExistingPatient(existing);
            }
          } catch (e) {
            setError('CPF já cadastrado no sistema');
          }
        } else {
          setError(detail);
        }
      } else {
        setError(err.response?.data?.detail || 'Erro ao cadastrar paciente');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="novo-paciente-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(-1)}
              data-testid="btn-voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Novo Paciente</h1>
              <p className="text-muted-foreground mt-1">
                Cadastre um novo paciente no sistema
              </p>
            </div>
          </div>
          
          {/* Import with AI Button */}
          <ImportarComIA 
            onImportComplete={(pacientes) => {
              if (pacientes?.length > 0) {
                navigate(`/pacientes/${pacientes[0].paciente_id}/internacao/${pacientes[0].internacao_id}`);
              }
            }}
          />
        </div>

        {/* Existing Patient Alert */}
        {existingPatient && (
          <Alert className="border-blue-500 bg-blue-50 dark:bg-blue-900/20" data-testid="existing-patient-alert">
            <UserCheck className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-700 dark:text-blue-400">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="font-medium">Paciente já cadastrado!</p>
                  <p className="text-sm">{existingPatient.nome} - CPF: {formatCPF(existingPatient.cpf)}</p>
                  {existingInternacao ? (
                    <p className="text-xs mt-1 text-orange-600">
                      ⚠️ Possui internação ativa desde {existingInternacao.data_internacao}
                    </p>
                  ) : (
                    <p className="text-xs mt-1 text-green-600">
                      ✓ Sem internação ativa - pode criar nova admissão
                    </p>
                  )}
                </div>
                <Link to={`/pacientes/${existingPatient.id}/nova-internacao`}>
                  <Button size="sm" className="gap-1" data-testid="btn-nova-admissao-existente">
                    <Plus className="h-4 w-4" />
                    {existingInternacao ? 'Ver Internação' : 'Nova Admissão'}
                  </Button>
                </Link>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>Dados do Paciente</CardTitle>
            <CardDescription>
              Preencha os campos obrigatórios para cadastrar o paciente
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive" data-testid="form-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="nome">Nome Completo *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="nome"
                      placeholder="Nome completo do paciente"
                      value={formData.nome}
                      onChange={(e) => handleChange('nome', e.target.value)}
                      required
                      disabled={!!existingPatient}
                      data-testid="input-nome"
                      className="flex-1"
                    />
                    <VoiceInput 
                      targetInputId="nome"
                      onTranscript={(text) => handleChange('nome', text)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cpf">CPF *</Label>
                  <div className="relative">
                    <Input
                      id="cpf"
                      placeholder="000.000.000-00"
                      value={formData.cpf}
                      onChange={(e) => handleChange('cpf', e.target.value)}
                      onBlur={handleCPFBlur}
                      maxLength={14}
                      required
                      disabled={!!existingPatient}
                      className={`${cpfError ? 'border-red-500' : existingPatient ? 'border-blue-500' : ''} ${cpfSearching ? 'pr-10' : ''}`}
                      data-testid="input-cpf"
                    />
                    {cpfSearching && (
                      <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    )}
                  </div>
                  {cpfError && (
                    <p className="text-sm text-red-500">{cpfError}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="data_nascimento">Data de Nascimento</Label>
                  <Input
                    id="data_nascimento"
                    type="date"
                    value={formData.data_nascimento}
                    onChange={(e) => handleChange('data_nascimento', e.target.value)}
                    data-testid="input-data-nascimento"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone / Contato</Label>
                  <Input
                    id="telefone"
                    placeholder="(00) 00000-0000"
                    value={formData.telefone}
                    onChange={(e) => handleChange('telefone', e.target.value)}
                    data-testid="input-telefone"
                  />
                </div>
              </div>

              {/* Address Section */}
              <div className="border-t pt-6">
                <h3 className="font-medium mb-4">Endereço</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cep">CEP</Label>
                    <div className="relative">
                      <Input
                        id="cep"
                        placeholder="00000-000"
                        value={formData.cep}
                        onChange={(e) => handleChange('cep', e.target.value)}
                        onBlur={handleCEPBlur}
                        maxLength={9}
                        className={cepError ? 'border-yellow-500' : ''}
                        data-testid="input-cep"
                      />
                      {cepLoading && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                      )}
                    </div>
                    {cepError && (
                      <p className="text-sm text-yellow-600">{cepError}</p>
                    )}
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="logradouro">Logradouro</Label>
                    <Input
                      id="logradouro"
                      placeholder="Rua, Avenida, etc."
                      value={formData.logradouro}
                      onChange={(e) => handleChange('logradouro', e.target.value)}
                      data-testid="input-logradouro"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="numero">Número</Label>
                    <Input
                      id="numero"
                      placeholder="123"
                      value={formData.numero}
                      onChange={(e) => handleChange('numero', e.target.value)}
                      data-testid="input-numero"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="complemento">Complemento</Label>
                    <Input
                      id="complemento"
                      placeholder="Apto, Bloco, etc."
                      value={formData.complemento}
                      onChange={(e) => handleChange('complemento', e.target.value)}
                      data-testid="input-complemento"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bairro">Bairro</Label>
                    <Input
                      id="bairro"
                      placeholder="Bairro"
                      value={formData.bairro}
                      onChange={(e) => handleChange('bairro', e.target.value)}
                      data-testid="input-bairro"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cidade">Cidade</Label>
                    <Input
                      id="cidade"
                      placeholder="Cidade"
                      value={formData.cidade}
                      onChange={(e) => handleChange('cidade', e.target.value)}
                      data-testid="input-cidade"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="uf">UF</Label>
                    <Input
                      id="uf"
                      placeholder="SP"
                      value={formData.uf}
                      onChange={(e) => handleChange('uf', e.target.value.toUpperCase())}
                      maxLength={2}
                      data-testid="input-uf"
                    />
                  </div>
                </div>
              </div>

              {/* Observations */}
              <div className="space-y-2">
                <Label htmlFor="observacoes">Observações Administrativas</Label>
                <Textarea
                  id="observacoes"
                  placeholder="Observações gerais sobre o paciente..."
                  value={formData.observacoes}
                  onChange={(e) => handleChange('observacoes', e.target.value)}
                  rows={3}
                  data-testid="input-observacoes"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => navigate(-1)}
                  data-testid="btn-cancelar"
                >
                  Cancelar
                </Button>
                {existingPatient ? (
                  <Link to={`/pacientes/${existingPatient.id}/nova-internacao`}>
                    <Button data-testid="btn-ir-nova-admissao">
                      <Plus className="h-4 w-4 mr-2" />
                      Criar Nova Admissão
                    </Button>
                  </Link>
                ) : (
                  <Button 
                    type="submit" 
                    disabled={loading || !!cpfError}
                    data-testid="btn-salvar"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Salvar e Criar Internação
                      </>
                    )}
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default NovoPaciente;
